import type { BaseQueryApi, FetchArgs, FetchBaseQueryError, FetchBaseQueryMeta, QueryReturnValue } from "@reduxjs/toolkit/query";
import apiEndPoints from "../../apiconstants";
import { api, handleErrorResponse } from "../api";

type OrgUsersBaseQuery = (args: FetchArgs) => Promise<QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>>;
import { ConversationHistoryAPIResponse, ConversationMessagesAPIResponse, ConversationPaginationPayload, GetConversationMessagePayload, TriageAPIResponse, TriagePaginationPayload, UpdateTriagePayload, UpdateTriageAPIResponse, TriageDetailsAPIResponse, TriageJiraAssignableUsersData, TriageTransitionsData, ShareConversationMailPayload, ShareConversationMailResponse, QueryOrgUsersPayload } from "./conversationtypes";

/**
 * Parent triage query API may return a `presigned_url` instead of inlining large table payloads.
 * Pagination/filters still go on the parent call; row data is loaded from the URL when present.
 * Presigned payload shape matches `response.data`.
 */
async function resolveTriageQueryData(response: any) {
  const data = response?.data;
  const presignedUrl = data?.presigned_url;
  if (typeof presignedUrl === 'string' && presignedUrl.length > 0) {
    const presignedResponse = await fetch(presignedUrl);
    if (!presignedResponse.ok) {
      throw new Error(
        `Failed to load triage data from presigned URL (${presignedResponse.status})`
      );
    }
    return presignedResponse.json();
  }
  return data;
}

type AuthLikeState = {
  auth?: {
    user?: { details?: Record<string, unknown> | null };
  };
};

/** `org_id` header required by /api/v2/users, read from the Auth0 profile. */
function orgIdHeaders(state: unknown): Record<string, string> | undefined {
  const details = (state as AuthLikeState)?.auth?.user?.details;
  const claim = details?.org_id ?? details?.['https://circuitry.ai/org_id'];
  const orgId = typeof claim === 'string' ? claim.trim() : '';
  return orgId ? { org_id: orgId } : undefined;
}

function unwrapResponseData(body: unknown): unknown {
  if (body && typeof body === 'object' && 'data' in body) {
    return (body as { data: unknown }).data;
  }
  return body;
}

const conversationAdvisorFilterId = (filters: { advisor_id?: unknown }[]) => {
  const advisorId = filters.find((filter) => filter.advisor_id)?.advisor_id;
  return typeof advisorId === 'string' ? advisorId : 'all';
};

// Define a service using a base URL and expected endpoints
export const conversationsApiSlice = api.injectEndpoints({
  endpoints: builder => ({
    getConversationMessages: builder.query<ConversationMessagesAPIResponse, GetConversationMessagePayload>({
      query: ({ sender_id, id }) =>
        `${apiEndPoints?.getConversationMessages}?sender_id=${sender_id ?? id ?? ''}`,
      providesTags: (_result, _error, payload) => [{
        type: "Conversations",
        id: String(payload.sender_id ?? payload.id ?? ''),
      }],
      transformResponse: (response: any) => response.data,
      transformErrorResponse: handleErrorResponse
    }),
    exportConversations: builder.mutation<any, any>({
      query: (payLoad) => ({
        url: apiEndPoints?.exportConversations,
        method: 'POST',
        body: payLoad,
      }),
      transformResponse: (response: any) => response.data,
      transformErrorResponse: handleErrorResponse,
    }),
    getConversationHistory: builder.mutation<ConversationHistoryAPIResponse, ConversationPaginationPayload>({
      query: (paginationData) => ({
        url: apiEndPoints?.getConversationHistory,
        method: 'POST',
        body: paginationData
      }),
      invalidatesTags: (result, error, advisorData) => [{
        type: 'Conversations',
        id: conversationAdvisorFilterId(advisorData.filters)
      }],
      transformResponse: (response: any) => response.data,
      transformErrorResponse: handleErrorResponse
    }),
    getConversationHistoryMilves: builder.mutation<ConversationHistoryAPIResponse, ConversationPaginationPayload>({
      query: (paginationData) => ({
        url: apiEndPoints?.getConversationHistoryMilves,
        method: 'POST',
        body: paginationData
      }),
      invalidatesTags: (result, error, advisorData) => [{
        type: 'Conversations',
        id: conversationAdvisorFilterId(advisorData.filters)
      }],
      transformResponse: (response: any) => response.data,
      transformErrorResponse: handleErrorResponse
    }),
    fetchConversationDetails: builder.mutation<ConversationMessagesAPIResponse, GetConversationMessagePayload>({
      query: ({ id, sender_id }) => ({
        url: `${apiEndPoints?.getConversationMessages}/${id ?? sender_id ?? ''}`,
        method: 'GET',
      }),
      transformResponse: (response: any) => response.data,
      transformErrorResponse: handleErrorResponse
    }),
    getSearchConversatios: builder.mutation<any, any>({
      query: (searchPayload) => ({
        url: `${apiEndPoints?.getSemanticSeachResults}`,
        method: 'POST',
        body: searchPayload
      }),
      transformResponse: (response: any) => response.data,
      transformErrorResponse: handleErrorResponse
    }),
    getFullTextSearchResults: builder.mutation<any, any>({
      query: (searchPayload) => ({
        url: `${apiEndPoints?.getFullTextSearchResults}`,
        method: 'POST',
        body: searchPayload
      }),
      transformResponse: (response: any) => response.data,
      transformErrorResponse: handleErrorResponse
    }),
    getTriageQuery: builder.mutation<TriageAPIResponse, TriagePaginationPayload>({
      query: (paginationData) => ({
        url: apiEndPoints?.getTriageQuery,
        method: 'POST',
        body: paginationData
      }),
      transformResponse: resolveTriageQueryData,
      transformErrorResponse: handleErrorResponse
    }),
    updateTriage: builder.mutation<UpdateTriageAPIResponse, UpdateTriagePayload>({
      query: ({ triage_id, ...body }) => ({
        url: `${apiEndPoints?.updateTriage}/${triage_id}`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (response: any) => response.data,
      transformErrorResponse: handleErrorResponse
    }),
    getTriageDetails: builder.mutation<TriageDetailsAPIResponse, string>({
      query: (triageId) => ({
        url: `${apiEndPoints?.getTriageDetails}/${triageId}`,
        method: 'GET',
      }),
      transformResponse: (response: any) => response.data,
      transformErrorResponse: handleErrorResponse
    }),
    getOrgUsers: builder.query<unknown, void>({
      queryFn: async (_arg: void, api: BaseQueryApi, _extra: unknown, baseQuery: OrgUsersBaseQuery) => {
        const result = await baseQuery({
          url: apiEndPoints?.getOrgUsers,
          method: 'GET',
          headers: orgIdHeaders(api.getState()),
        });
        if (result.error) return { error: handleErrorResponse(result.error) };
        return { data: unwrapResponseData(result.data) };
      },
    }),
    queryOrgUsers: builder.mutation<unknown, QueryOrgUsersPayload>({
      queryFn: async ({ name }: QueryOrgUsersPayload, api: BaseQueryApi, _extra: unknown, baseQuery: OrgUsersBaseQuery) => {
        const result = await baseQuery({
          url: apiEndPoints?.queryOrgUsers,
          method: 'POST',
          headers: orgIdHeaders(api.getState()),
          body: {
            filters: [{ name, condition: 'contains' }],
            pagination: { page: 1, per_page: 50 },
          },
        });
        if (result.error) return { error: handleErrorResponse(result.error) };
        return { data: unwrapResponseData(result.data) };
      },
    }),
    getTriageJiraAssignableUsers: builder.mutation<TriageJiraAssignableUsersData, void>({
      query: () => ({
        url: apiEndPoints?.getTriageJiraAssignableUsers,
        method: 'GET',
      }),
      transformResponse: (response: any) => (response?.data ?? response) as TriageJiraAssignableUsersData,
      transformErrorResponse: handleErrorResponse
    }),
    getTriageTransitions: builder.mutation<TriageTransitionsData, string>({
      query: (triageId) => ({
        url: `${apiEndPoints?.getTriageDetails}/${triageId}/transitions`,
        method: 'GET',
      }),
      transformResponse: (response: any) => (response?.data ?? response) as TriageTransitionsData,
      transformErrorResponse: handleErrorResponse
    }),
    downloadConversation: builder.query<any, { sender_id: string }>({
      query: ({ sender_id }) => ({
        url: `${apiEndPoints?.downloadConversation}/${sender_id}/pdf`,
        method: 'GET',
      }),
      transformResponse: (response: any) => response.data,
      transformErrorResponse: handleErrorResponse
    }),
    shareConversationMail: builder.mutation<ShareConversationMailResponse, ShareConversationMailPayload>({
      query: (body) => ({
        url: apiEndPoints?.shareConversationMail,
        method: 'POST',
        body,
      }),
      transformResponse: (response: ShareConversationMailResponse) => response,
      transformErrorResponse: handleErrorResponse
    })
  })
});

// Hooks are auto-generated by RTK-Query
// Same as `advisorApiSlice.endpoints.getQuotes.useQuery`
export const { useGetConversationMessagesQuery, useExportConversationsMutation, useGetConversationHistoryMutation, useGetConversationHistoryMilvesMutation, useFetchConversationDetailsMutation,
  useGetSearchConversatiosMutation,
  useGetFullTextSearchResultsMutation,
  useGetTriageQueryMutation,
  useUpdateTriageMutation,
  useGetTriageDetailsMutation,
  useGetOrgUsersQuery,
  useLazyGetOrgUsersQuery,
  useQueryOrgUsersMutation,
  useGetTriageJiraAssignableUsersMutation,
  useGetTriageTransitionsMutation,
  useLazyDownloadConversationQuery,
  useShareConversationMailMutation
} = conversationsApiSlice